import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import '../../styles/CommonStyles.css';
import authService from '../../services/authService'; // Service for handling authentication API calls

function MemberLoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true  // Changed from false to true
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
    
    // Clear general login error when any field changes
    if (loginError) {
      setLoginError('');
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setLoginError('');
    
    try {
      // Call the authService to handle login, passing email and password to match backend API
      const response = await authService.memberLogin({
        email: formData.email,
        password: formData.password
      }, formData.rememberMe);
      
      // If login is successful, store tokens and user info
      if (response && response.token) {
        // Store the auth token and user data
        localStorage.setItem('token', response.token);
        
        if (response.user) {
          localStorage.setItem('user', JSON.stringify(response.user));
        }
        
        // Check if membership has expired
        const isExpired = response.member.expiry_date 
          ? new Date(response.member.expiry_date) < new Date() 
          : false;
        
        // Check if member status is pending (awaiting initial payment)
        if (response.member && response.member.status === 'pending') {
          localStorage.setItem('paymentRequired', 'true');
          navigate('/complete-payment', { 
            state: { 
              memberId: response.member.id,
              memberName: response.member.name,
              membershipType: response.member.membership_type,
              isRenewal: false
            } 
          });
          return;
        }
        
        // Check payment status - only require payment if unpaid AND expired
        if (response.member && response.member.payment_status === 'unpaid' && isExpired) {
          // Store that payment is required
          localStorage.setItem('paymentRequired', 'true');
          // Redirect to payment completion page
          navigate('/complete-payment', { 
            state: { 
              memberId: response.member.id,
              memberName: response.member.name,
              membershipType: response.member.membership_type,
              isRenewal: true,
              expiryDate: response.member.expiry_date
            } 
          });
          return;
        }
        
        // Check if membership is expired (even if payment_status is paid)
        if (isExpired) {
          // Membership expired, redirect to renewal page
          localStorage.setItem('paymentRequired', 'true');
          navigate('/complete-payment', { 
            state: { 
              memberId: response.member.id,
              memberName: response.member.name,
              membershipType: response.member.membership_type,
              isRenewal: true,
              expiryDate: response.member.expiry_date
            } 
          });
          return;
        }
        
        // Payment is complete and membership is active, redirect to member dashboard
        localStorage.removeItem('paymentRequired');
        navigate('/programs');
      } else {
        // Handle case when response doesn't contain expected data
        setLoginError('Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      // Show more specific error message if available from the API
      setLoginError(error.response?.data?.error || error.message || 'Invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <Header />
      
      <div className="page-header">
        <h1>MEMBER LOGIN</h1>
        <p>Access your BVA member account</p>
      </div>
      
      <section className="content-section">
        <div className="login-container" style={{ 
          maxWidth: '500px', 
          margin: '0 auto',
          padding: '2rem',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: 'var(--box-shadow)'
        }}>
          <h2 style={{ 
            textAlign: 'center', 
            marginBottom: '2rem',
            color: 'var(--primary-color)'
          }}>
            Member Login
          </h2>
          
          {loginError && (
            <div style={{ 
              padding: '0.75rem 1rem',
              marginBottom: '1.5rem',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '4px',
              border: '1px solid #f5c6cb'
            }}>
              {loginError}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label 
                htmlFor="email" 
                style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600' 
                }}
              >
                Email Address
              </label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.email ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              {errors.email && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.email}
                </p>
              )}
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label 
                htmlFor="password" 
                style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600' 
                }}
              >
                Password
              </label>
              <input 
                type="password" 
                id="password" 
                name="password" 
                value={formData.password}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.password ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              {errors.password && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.password}
                </p>
              )}
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input 
                  type="checkbox" 
                  id="rememberMe" 
                  name="rememberMe" 
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem' }}
                />
                <label htmlFor="rememberMe">Remember me</label>
              </div>
              
              <Link to="/forgot-password" style={{ 
                color: 'var(--primary-color)',
                textDecoration: 'none',
                fontWeight: '500'
              }}>
                Forgot password?
              </Link>
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isSubmitting}
              style={{ 
                width: '100%',
                padding: '0.8rem',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                fontSize: '1rem',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? 'LOGGING IN...' : 'LOGIN'}
            </button>
          </form>
          
          <div style={{ 
            textAlign: 'center', 
            marginTop: '2rem',
            borderTop: '1px solid #eee',
            paddingTop: '1.5rem'
          }}>
            <p style={{ marginBottom: '1rem' }}>
              Not a member yet?
            </p>
            <Link to="/register">
              <button 
                className="btn btn-light"
                style={{ 
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #ddd',
                  color: 'var(--primary-color)',
                  padding: '0.8rem 1.5rem'
                }}
              >
                REGISTER NOW
              </button>
            </Link>
          </div>
        </div>
        
        <div style={{ 
          maxWidth: '500px', 
          margin: '2rem auto 0',
          textAlign: 'center'
        }}>
          <h3 style={{ marginBottom: '1rem', color: 'var(--primary-color)' }}>
            Benefits of BVA Membership
          </h3>
          <ul style={{ 
            textAlign: 'left',
            listStylePosition: 'inside',
            paddingLeft: '1rem' 
          }}>
            <li>Access to exclusive educational resources</li>
            <li>Discounted rates for events and workshops</li>
            <li>Professional networking opportunities</li>
            <li>Stay updated with industry insights</li>
          </ul>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default MemberLoginPage;